/**
 * Anonymous Spot sales chat API (no Auth0 / Bearer required).
 */

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export type SpotChatRole = 'user' | 'assistant';

export type SpotChatMessage = {
  role: SpotChatRole;
  content: string;
};

export type SpotPageSnapshot = {
  text: string;
  links: Array<{ label: string; href: string }>;
  imageDescriptions: string[];
};

export type SpotChatStreamHandlers = {
  onDelta?: (delta: string) => void;
  onFinal?: (content: string) => void;
  onError?: (message: string) => void;
};

function parseSseBlock(raw: string): { eventName: string; data: string } | null {
  const lines = raw.split('\n');
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (!dataLines.length) return null;
  const data = dataLines.join('\n');
  if (data === '[DONE]') {
    return { eventName: 'done', data };
  }
  return { eventName, data };
}

function hasStringField<T, K extends string>(value: T, key: K): value is T & Record<K, string> {
  if (value === null || typeof value !== 'object') return false;
  const descriptor = Object.getOwnPropertyDescriptor(value, key);
  return descriptor !== undefined && typeof descriptor.value === 'string';
}

function parseTextField(raw: string, field: string): string | null {
  try {
    const payload: unknown = JSON.parse(raw);
    return hasStringField(payload, field) ? payload[field] : null;
  } catch {
    return null;
  }
}

function publicErrorMessage(raw: string, fallback: string): string {
  return parseTextField(raw, 'message')?.trim() || fallback;
}

export async function streamSpotChat(params: {
  message: string;
  history: SpotChatMessage[];
  pagePath: string;
  siteOrigin: string;
  pageSnapshot: SpotPageSnapshot;
  sessionId: string;
  signal?: AbortSignal;
  handlers: SpotChatStreamHandlers;
}): Promise<void> {
  if (!API_BASE) {
    params.handlers.onError?.('Chat is unavailable right now (API URL missing).');
    return;
  }

  const response = await fetch(`${API_BASE}/public/spot-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      message: params.message,
      history: params.history,
      pagePath: params.pagePath,
      siteOrigin: params.siteOrigin,
      pageSnapshot: params.pageSnapshot,
      sessionId: params.sessionId,
    }),
    signal: params.signal,
  });

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    params.handlers.onError?.(
      publicErrorMessage(text, 'Spot is temporarily unavailable. Try again in a moment.'),
    );
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let assembled = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    while (true) {
      const boundary = buffer.indexOf('\n\n');
      if (boundary === -1) break;
      const rawEvent = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const parsed = parseSseBlock(rawEvent);
      if (!parsed) continue;
      if (parsed.eventName === 'done') {
        params.handlers.onFinal?.(assembled.trim());
        return;
      }
      if (parsed.eventName === 'message_delta') {
        const delta = parseTextField(parsed.data, 'delta');
        if (delta) {
          assembled += delta;
          params.handlers.onDelta?.(delta);
        }
      } else if (parsed.eventName === 'message') {
        const content = parseTextField(parsed.data, 'content');
        if (content?.trim()) assembled = content;
      } else if (parsed.eventName === 'error') {
        params.handlers.onError?.(
          publicErrorMessage(parsed.data, 'Spot could not answer just now.'),
        );
        return;
      }
    }
  }

  params.handlers.onFinal?.(assembled.trim());
}

export function getSpotBubbleDelayMs(): number {
  const fromEnv = Number(process.env.REACT_APP_SPOT_CHAT_BUBBLE_DELAY_MS);
  if (Number.isFinite(fromEnv) && fromEnv >= 0) {
    return fromEnv;
  }
  // Default: wait 20s so Spot does not interrupt the first glance.
  return 20_000;
}
